import { safeErrorDetails } from "./utils/logDiagnostics";
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";

import LoginPage from "./pages/authPages/LoginPage";
import EmailVerifiedLoginPage from "./pages/authPages/EmailVerifiedLoginPage";
import RegisterPage from "./pages/authPages/RegisterPage";
import MarketingUnsubscribe from './pages/MarketingUnsubscribe';
import MarketingNoticePrompt from './components/marketing/MarketingNoticePrompt';
import AnalyticsControls from './analytics/AnalyticsControls';
import PrivacyPreferences from './pages/PrivacyPreferences';
import SupplierRegisterPage from "./pages/authPages/SupplierRegisterPage";
import ForgotPasswordPage from "./pages/authPages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/authPages/ResetPasswordPage";

import Dashboard from "./pages/Dashboard";
import RicercaServizi from "./pages/RicercaServizi";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import CheckoutCancel from "./pages/CheckoutCancel";
import PaymentReceiptPage from "./pages/PaymentReceiptPage";
import PayPalReturn from "./pages/PayPalReturn";
import Servizi from "./pages/Servizi";
import FornitoreServizi from "./pages/FornitoreServizi";
import Profilo from "./pages/Profilo";

import Ordini from "./pages/Ordini/Ordini";
import OrdineDettaglio from "./pages/Ordini/OrdineDettaglio";
import FornitoreOrdini from "./pages/Ordini/FornitoreOrdini";
import FornitoreOrdineDettaglio from "./pages/Ordini/FornitoreOrdineDettaglio";
import AdminOrdini from "./pages/Ordini/AdminOrdini";

import Header from "./components/Header";
import Footer from "./components/Footer";
import PrivateRoute from "./components/PrivateRoute";
import AdminRoute from "./components/AdminRoute";
import CartModal from "./components/CartModal";
import AppUrlListener from "./components/AppUrlListener";

import AdminDashboard from "./Admin/AdminDashboard";
import AdminStatistics from './Admin/AdminStatistics';
import AdminSiteNav from "./Admin/AdminSiteNav";
import AdminApp from "./Admin/AdminApp";
import AdminApprovals from "./Admin/AdminApprovals";
import AdminGdpr from "./Admin/AdminGdpr";
import AdminServiziCatalogo from "./Admin/AdminServiziCatalogo";
import AdminFornitori from "./Admin/AdminFornitori";
import AdminClientsPage from "./Admin/AdminClienti";
import CrossSellAdmin from "./Admin/CrossSellAdmin";
import AdminAccountingPercentuali from "./Admin/AdminAccountingPercentuali";
import AdminWalletTopupOffers from "./Admin/AdminWalletTopupOffers";
import AdminZones from "./Admin/AdminZones";

import AdminFornitoriServizi from "./pages/AdminFornitoriServizi";

import PromozioniLayout from "./pages/admin/Promozioni/PromozioniLayout";
import PromoList from "./pages/admin/Promozioni/PromoList";
import PromoReferralConfig from "./pages/admin/Promozioni/PromoReferralConfig";
import PromoWelcomeConfig from "./pages/admin/Promozioni/PromoWelcomeConfig";
import AdminPromozioniClientiStats from "./pages/admin/Promozioni/AdminPromozioniClientiStats";
import AdminPromozioniSegmentiStats from "./pages/admin/Promozioni/AdminPromozioniSegmentiStats";

import AdminSubscriptionsPlans from "./pages/admin/AdminSubscriptionsPlans";
import AdminSubscriptionsRequests from "./pages/admin/AdminSubscriptionsRequests";
import FornitoreAbbonamenti from "./pages/fornitore/FornitoreAbbonamenti";

import AdminAccountingOrdini from "./pages/admin/AdminAccountingOrdini";
import AdminAccountingFatturazione from "./pages/admin/AdminAccountingFatturazione";
import AdminAccountingAbbonamenti from "./pages/admin/AdminAccountingAbbonamenti";
import AdminAccountingFattureEmesse from "./pages/admin/AdminAccountingFattureEmesse";
import AdminAccountingPagamentiFornitori from "./pages/admin/AdminAccountingPagamentiFornitori";
import useGlobalPullToRefresh from "./hooks/useGlobalPullToRefresh";
import PullToRefreshIndicator from "./components/PullToRefreshIndicator";
import usePreventIOSInputZoom from "./hooks/usePreventIOSInputZoom";
import AdminOrganizzazioni from "./pages/admin/AdminOrganizzazioni";
import AbbonamentiCliente from "./pages/AbbonamentiCliente";
import AdminSubscriptionsList from "./pages/admin/AdminSubscriptionsList";
import CreditoPrepagato from "./pages/CreditoPrepagato";
import InvitaAmico from "./pages/InvitaAmico";
import AbbonamentiConfronto from "./pages/AbbonamentiConfronto";
import AbbonamentoAttiva from "./pages/AbbonamentoAttiva";
import FornitoreCalendario from "./pages/fornitore/FornitoreCalendario";
import { Capacitor } from "@capacitor/core";
import { initPush } from "./notifications/push";
import { useNavigate } from "react-router-dom";
import { setPushNavigationHandler } from "./notifications/push";
import { Toaster } from "react-hot-toast";
import { isNativeApp } from "./utils/nativeNavigation";

function AuthLayout() {
  return <Outlet />;
}

function MainLayout() {
  const { pathname } = useLocation();
  // Il footer-sito serve alle pagine CLIENTE (continuità con la vetrina), non
  // al pannello admin (strumento interno) — coerente con AdminHeader che non
  // mostra le voci-sito.
  const isAdminArea =
    pathname.startsWith("/admin") || pathname.startsWith("/react-admin");

  return (
    <>
      <Header />
      <Outlet />
      {!isAdminArea && <Footer />}
      <CartModal />
    </>
  );
}

function PushNavigationBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    setPushNavigationHandler((to) => {
      if (!to) return;
      navigate(to);
    });

    return () => {
      setPushNavigationHandler(null);
    };
  }, [navigate]);

  return null;
}

export default function App() {
  const ptr = useGlobalPullToRefresh({
    onRefresh: async () => {
      window.location.reload();
    },
  });

  usePreventIOSInputZoom();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    if (!Capacitor.isNativePlatform()) return;

    initPush().catch((err) => {
      console.error("Errore init push:", safeErrorDetails(err));
    });
  }, []);

  return (
    <>
      <PullToRefreshIndicator
        pullDistance={ptr.pullDistance}
        visible={ptr.visible}
        isReady={ptr.isReady}
        isRefreshing={ptr.isRefreshing}
      />

      <div className={`app-safe-shell${isNativeApp() ? " app-safe-shell--native" : ""}`}>
        <Router>
          <AppUrlListener />
          <PushNavigationBridge />
          <MarketingNoticePrompt />
          <AnalyticsControls />
          <Toaster position="top-center" toastOptions={{ duration: 3500 }} />

          <Routes>
            <Route path="/privacy/preferenze" element={<PrivacyPreferences/>}/>
            <Route element={<AuthLayout />}>
              <Route path="/" element={<LoginPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/email-confermata"
                element={<EmailVerifiedLoginPage />}
              />
              <Route path="/register" element={<RegisterPage />} />
              <Route
                path="/supplier-register"
                element={<SupplierRegisterPage />}
              />
              <Route path="/forgot" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
            </Route>

            <Route element={<MainLayout />}>
              <Route path="/marketing/unsubscribe" element={<MarketingUnsubscribe/>}/>
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/checkout/cancel" element={<CheckoutCancel />} />
              <Route
                path="/checkout/paypal-cancel"
                element={<CheckoutCancel />}
              />
              <Route
                path="/checkout/paypal-return"
                element={<PayPalReturn />}
              />

              <Route path="/servizi/:id" element={<Servizi />} />
              <Route path="/fornitore/servizi" element={<FornitoreServizi />} />
              <Route
                path="/pagamenti/:paymentId"
                element={<PaymentReceiptPage />}
              />
              <Route
                path="/fornitore/calendario"
                element={
                  <PrivateRoute>
                    <FornitoreCalendario />
                  </PrivateRoute>
                }
              />

              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Dashboard />
                  </PrivateRoute>
                }
              />

              <Route
                path="/ricercaservizi"
                element={
                  <PrivateRoute>
                    <RicercaServizi />
                  </PrivateRoute>
                }
              />

              <Route
                path="/abbonamenti"
                element={
                  <PrivateRoute>
                    <AbbonamentiCliente />
                  </PrivateRoute>
                }
              />

              <Route
                path="/abbonamenti/confronto"
                element={
                  <PrivateRoute>
                    <AbbonamentiConfronto />
                  </PrivateRoute>
                }
              />

              <Route
                path="/abbonamenti/attiva/:planId"
                element={
                  <PrivateRoute>
                    <AbbonamentoAttiva />
                  </PrivateRoute>
                }
              />

              <Route
                path="/ordini"
                element={
                  <PrivateRoute>
                    <Ordini />
                  </PrivateRoute>
                }
              />

              <Route path="/ordini/:id" element={<OrdineDettaglio />} />
              <Route path="/profilo" element={<Profilo />} />
              <Route path="/credito-prepagato" element={<CreditoPrepagato />} />
              <Route path="/invita-amico" element={<InvitaAmico />} />
              <Route path="/fornitore/ordini" element={<FornitoreOrdini />} />
              <Route
                path="/fornitore/abbonamenti"
                element={
                  <PrivateRoute>
                    <FornitoreAbbonamenti />
                  </PrivateRoute>
                }
              />
              <Route
                path="/fornitore/ordini/:id"
                element={<FornitoreOrdineDettaglio />}
              />

              <Route
                path="/react-admin/*"
                element={
                  <PrivateRoute>
                    <AdminApp />
                  </PrivateRoute>
                }
              />

              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/statistiche"
                element={<AdminRoute><AdminStatistics /></AdminRoute>}
              />

              <Route
                path="/admin/header-footer"
                element={
                  <AdminRoute>
                    <AdminSiteNav />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/approvals"
                element={
                  <AdminRoute>
                    <AdminApprovals />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/gdpr"
                element={
                  <AdminRoute>
                    <AdminGdpr />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/servizi"
                element={
                  <AdminRoute>
                    <AdminServiziCatalogo />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/promos"
                element={
                  <AdminRoute>
                    <Navigate to="/admin/promozioni/list" replace />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/promozioni"
                element={
                  <AdminRoute>
                    <PromozioniLayout />
                  </AdminRoute>
                }
              >
                <Route index element={<Navigate to="list" replace />} />
                <Route path="welcome" element={<PromoWelcomeConfig />} />
                <Route path="referral" element={<PromoReferralConfig />} />
                <Route path="list" element={<PromoList />} />
              </Route>

              <Route
                path="/admin/promozioni/clienti"
                element={
                  <AdminRoute>
                    <AdminPromozioniClientiStats />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/promozioni/segmenti"
                element={
                  <AdminRoute>
                    <AdminPromozioniSegmentiStats />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/fornitori"
                element={
                  <AdminRoute>
                    <AdminFornitori />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/clienti"
                element={
                  <AdminRoute>
                    <AdminClientsPage />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/cross-sell"
                element={
                  <AdminRoute>
                    <CrossSellAdmin />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/ordini"
                element={
                  <AdminRoute>
                    <AdminOrdini />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/gestioneservizifornitore"
                element={
                  <AdminRoute>
                    <AdminFornitoriServizi />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/contabilita/percentuali"
                element={
                  <AdminRoute>
                    <AdminAccountingPercentuali />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/contabilita/ordini"
                element={
                  <AdminRoute>
                    <AdminAccountingOrdini />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/contabilita/fatturazione"
                element={
                  <AdminRoute>
                    <AdminAccountingFatturazione />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/contabilita/abbonamenti"
                element={
                  <AdminRoute>
                    <AdminAccountingAbbonamenti />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/contabilita/fatture"
                element={
                  <AdminRoute>
                    <AdminAccountingFattureEmesse />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/contabilita/pagamentifornitori"
                element={
                  <AdminRoute>
                    <AdminAccountingPagamentiFornitori />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/wallet-topup-offers"
                element={
                  <AdminRoute>
                    <AdminWalletTopupOffers />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/abbonamenti/piani"
                element={
                  <AdminRoute>
                    <AdminSubscriptionsPlans />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/abbonamenti/richieste"
                element={
                  <AdminRoute>
                    <AdminSubscriptionsRequests />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/abbonamenti/sottoscrizioni"
                element={
                  <AdminRoute>
                    <AdminSubscriptionsList />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/zones"
                element={
                  <AdminRoute>
                    <AdminZones />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/organizzazioni"
                element={
                  <AdminRoute>
                    <AdminOrganizzazioni />
                  </AdminRoute>
                }
              />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </div>
    </>
  );
}
